import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { fadeIn, fadeInOut, slideInOut, routerTransition } from '../shared/constants/animations';
import { SnackbarService } from '../core/services/snackbar/snackbar.service';
import { ToastClassEnum } from '../core/services/snackbar/snackbar.models';
import { SidenavConfig } from '../shared/models/generic.models';
import { PaperAppModalComponent } from './features/accounts/modals/paper-app-modal/paper-app-modal.component';
import { AccountUpdateMainModalComponent } from './features/account-request-workflows/modals/account-update-main-modal/account-update-main-modal.component';
import { UserModel, UsersClient } from '../core/clients/generated/client';
import { PlanningDeskRequestComponent } from './features/account-request-workflows/planning-desk-request/planning-desk-request.component';
import { PortfolioAnalysisCategoryEnum } from './features/account-request-workflows/planning-desk-request/enums/portfolio-analysis-category.enum';
import { Permission } from 'src/app/core/clients/generated/client';
import { CookieService } from 'ngx-cookie-service';
//import { portalV2OptInCookieName } from '../shared/constants/opt-in-cookie.constant';
import { AuthenticationService } from '../core/auth/authentication.service';
import { OpenSsoAgentSelectModalComponent } from '../shared/modals/sso-agent-select-modal/sso-agent-select-modal.component';
import { ReleaseNotesComponent } from '../shared/components/release-notes/release-notes.component';

@Component({
  animations: [ fadeIn, fadeInOut, slideInOut, routerTransition ],
  selector: 'app-portal',
  templateUrl: './portal.component.html',
})
export class PortalComponent implements OnInit, OnDestroy {
  private breakpointSubscription: Subscription;
  private sessionPollingSubcription?: Subscription;
  perm = Permission;
  portfolioAnalysisCategoryEnum = PortfolioAnalysisCategoryEnum;
  acmPortalSidenavConfig: SidenavConfig;
  loading = false;
  mediaMobile = false;
  title = ' | ';
  user?: UserModel;
  userSidenavConfig: SidenavConfig = {
    IsOpened: false,
    Mode: 'side',
  };

  //If both banners are set to be visible at the same time, the system message banner will override the new release banner.
  notificationBanners = [
    {
      Type: 'system message',  //'system message'  or 'new releases'
      StartDate: new Date('July 1, 2025'),
      EndDate: new Date('May 19, 2026 8:00:00'),
      Show: false,  //DO NOT CHANGE Show VALUE HERE, INSTEAD CHANGE ENDDATE ONLY TO BE VISIBLE (SEE OnInit())
    }
  ];

  @ViewChild('userSidenav') userSidenav!: MatSidenav;
  @ViewChild('acmPortalSidenav') acmPortalSidenav!: MatSidenav;
  constructor (
    private breakpointObserver: BreakpointObserver,
    private usersClient: UsersClient,
    private dialog: MatDialog,
    private router: Router,
    private snackbarService: SnackbarService,
    private titleService: Title,
    private cookieService:CookieService,
    private authenticationService: AuthenticationService
  ) {
    //define main sidenav config here to ensure it loads first to avoid stuttering issue
    this.acmPortalSidenavConfig = {
      IsOpened: true,
      Mode: 'over',
    };

    this.router.events
      .subscribe(() => {
        const sideNavContainer = document.querySelector('.mat-sidenav-content');
        if (sideNavContainer) sideNavContainer.scrollTop = 0;
      });

    this.breakpointSubscription = this.configureLayOut();

    const date = new Date();
    this.notificationBanners.forEach(banner => {
      if (date >= banner.StartDate && date <= banner.EndDate){
        banner.Show = true;
      }
    });

    //if both banners are Show = true, hide the new release banner.
    if (this.notificationBanners.filter(x => x.Show).length > 1){
      this.notificationBanners.filter(x => x.Type === 'new releases')[0].Show = false;
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.titleService.setTitle(this.title + 'Alphastar Portal');
    this.usersClient.getUserSelf()
      .subscribe({
        next: result => {
          this.user = result;
        }, error: () => this.snackbarService.openSnackbar('There was an error getting user info.')
      })
      .add(() => this.loading = false);

    this.sessionPollingSubcription = this.authenticationService.pollSessionExpirationSubcription();
  }

  ngOnDestroy(): void {
    this.breakpointSubscription.unsubscribe();
    this.sessionPollingSubcription?.unsubscribe();
  }

  configureLayOut(): Subscription {
    return this.breakpointObserver.observe([
      Breakpoints.TabletLandscape,
      Breakpoints.WebLandscape
    ]).subscribe({
      next: result => {
        if (result.matches) {
          this.mediaMobile = false;
          this.acmPortalSidenavConfig.IsOpened = true;
          this.acmPortalSidenavConfig.Mode = 'side';
          this.userSidenavConfig.Mode = 'over';
        } else {
          this.mediaMobile = true;
          this.acmPortalSidenavConfig.IsOpened = false;
          this.acmPortalSidenavConfig.Mode = 'push';
          this.userSidenavConfig.Mode = 'push';
        }
      },
      error: () => {
        this.snackbarService.openSnackbar('Error configuring root layout', ToastClassEnum.warning);
      }
    });
  }

  openReleaseNotesModal(): void {
    this.dialog.open(ReleaseNotesComponent);
  }

  openAccountUpdateModal(): void {
    this.dialog.open(AccountUpdateMainModalComponent, {
      autoFocus: false,
    });
  }

  openPortfolioRequestModal(category: PortfolioAnalysisCategoryEnum | null = null): void {
    this.dialog.open(PlanningDeskRequestComponent, { data: category });
  }

  openPaperAccountModal(): void {
    this.dialog.open(PaperAppModalComponent, {
      autoFocus: false,
    });
  }

  toggleUserSidenav(): void {
    this.userSidenav.toggle();
    if (this.mediaMobile) {
      if (this.acmPortalSidenav.opened) this.acmPortalSidenav.close();
    }
  }

  openOrionAgentSelectModal(destination:string|null): void {
    this.dialog.open(OpenSsoAgentSelectModalComponent, {
      minHeight: '28rem',
      autoFocus: false,
      data: {BaseUrl: 'api/sso/orion/portal/' + destination + '?appearAsCrmAgentId='}
    });
  }
}
